import type { Chord } from '@tunasong/music-lib'
import type { MelodyNote, Scale } from '@tunasong/schemas'
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { FeedbackStrip } from './feedback-strip.js'
import { mapKeyToPitch } from './melody-keyboard-input.js'
import { useMelodyPlayback } from './melody-playback.js'
import { MelodyStaff } from './melody-staff.js'
import styles from './melody-writer.module.css'

const BEATS_PER_CHORD = 4
const DEFAULT_TEMPO_BPM = 100

export interface MelodyWriterProps {
  /** Section context — scale + chords come from useMusicContext at the call site. */
  scale: Scale | null | undefined
  chords: Chord[]
  /** Drawer open/closed state — controlled by the section trigger. */
  open: boolean
  onClose: () => void
  /**
   * Optional starting notes for the writer's editing state. Hosts that
   * persist the melody supply this from the persisted entity; hosts that
   * don't persist supply an empty array or omit.
   */
  initialNotes?: MelodyNote[]
  /** Called whenever the user changes the melody notes — host persists. */
  onNotesChange?: (notes: MelodyNote[]) => void
}

/**
 * The Melody Writer drawer. Layer-1 surface only in this milestone:
 * staff input, classification chips, playback. Subsequent milestones
 * add persistence (M4) and L2-L4 affordances (M5-M7).
 *
 * The drawer is a thin host: it renders nothing when closed (so the inner
 * `useMelodyPlayback` audio hook is not invoked outside an audio-engine
 * context). When open, it delegates to `MelodyWriterContent`.
 *
 * Spec: docs/superpowers/specs/2026-05-17-melody-writer-design.md
 */
export const MelodyWriter: FC<MelodyWriterProps> = props => {
  const { scale, chords, open } = props
  if (!open || !scale || chords.length === 0) {
    return null
  }
  return <MelodyWriterContent {...props} scale={scale} />
}

interface MelodyWriterContentProps extends Omit<MelodyWriterProps, 'scale'> {
  scale: Scale
}

const MelodyWriterContent: FC<MelodyWriterContentProps> = props => {
  const { scale, chords, onClose, initialNotes, onNotesChange } = props
  const [notes, setNotes] = useState<MelodyNote[]>(initialNotes ?? [])
  const [tempoBpm, setTempoBpm] = useState(DEFAULT_TEMPO_BPM)
  const { play } = useMelodyPlayback({ notes, chords, tempoBpm })

  useEffect(() => {
    onNotesChange?.(notes)
  }, [notes, onNotesChange])

  /**
   * Scale-degree keystroke listener. v1 heuristic: keystrokes append a note
   * at the last chord position (no beat cursor yet — a follow-up task adds
   * one).
   */
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') {
        return
      }
      const pitch = mapKeyToPitch(e.key, scale)
      if (!pitch) {
        return
      }
      e.preventDefault()
      const lastChordIdx = chords.length - 1
      setNotes(prev => [
        ...prev,
        { pitch, octave: 4, duration: '4n', position: lastChordIdx * BEATS_PER_CHORD },
      ])
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [scale, chords])

  const handlePlace = (n: MelodyNote) => setNotes(prev => [...prev, n])
  const handleRemove = (n: MelodyNote) =>
    setNotes(prev => prev.filter(p => !(p.pitch === n.pitch && p.position === n.position)))

  return (
    <div className={styles.drawer} role="dialog" aria-label="Melody writer">
      <div className={styles.header}>
        <h2 className={styles.title}>Melody Writer</h2>
        <div className={styles.controls}>
          <button type="button" onClick={play} aria-label="Play melody">
            Play
          </button>
          <label className={styles.tempoLabel}>
            Tempo
            <input
              type="range"
              min={50}
              max={180}
              value={tempoBpm}
              aria-label="Tempo BPM"
              aria-valuenow={tempoBpm}
              onChange={e => setTempoBpm(Number(e.target.value))}
            />
            <span>{tempoBpm} BPM</span>
          </label>
        </div>
        <button type="button" aria-label="Close melody writer" onClick={onClose}>
          ×
        </button>
      </div>
      <div className={styles.body}>
        <MelodyStaff scale={scale} chords={chords} notes={notes} onPlaceNote={handlePlace} onRemoveNote={handleRemove} />
        <FeedbackStrip scale={scale} chords={chords} notes={notes} />
      </div>
    </div>
  )
}
